import React, { useCallback, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
} from "@mui/material";
import apiClient from "@/utils/api-client";
import { trackSentryException } from "@/utils/helpers";

type ImportTab = "text" | "csv" | "image";

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  onImported: (markdown: string) => void;
  organisationId: string;
}

const readFileAsBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // strip data URI prefix
      resolve(result.includes(",") ? result.split(",")[1] : result);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const readFileAsText = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string) ?? "");
    reader.onerror = reject;
    reader.readAsText(file);
  });

const ImportDialog = ({
  open,
  onClose,
  onImported,
  organisationId,
}: ImportDialogProps) => {
  const [tab, setTab] = useState<ImportTab>("text");
  const [text, setText] = useState("");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setText("");
    setCsvFile(null);
    setImageFiles([]);
    setError(null);
  }, []);

  const handleClose = useCallback(() => {
    if (loading) return;
    reset();
    onClose();
  }, [loading, onClose, reset]);

  const submit = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const payload: Record<string, unknown> = {
        org_id: organisationId,
        content_type: tab,
      };

      if (tab === "text") {
        if (!text.trim()) {
          setError("Please paste some text to import.");
          setLoading(false);
          return;
        }
        payload.text = text;
      } else if (tab === "csv") {
        if (csvFile == null) {
          setError("Please select a CSV file.");
          setLoading(false);
          return;
        }
        payload.text = await readFileAsText(csvFile);
        payload.filename = csvFile.name;
      } else {
        if (imageFiles.length === 0) {
          setError("Please select at least one image.");
          setLoading(false);
          return;
        }
        payload.images = await Promise.all(
          imageFiles.map(async (file) => ({
            filename: file.name,
            mime_type: file.type || "image/png",
            base64: await readFileAsBase64(file),
          }))
        );
      }

      const response = await apiClient.post<{ markdown: string }>(
        "/convert_to_markdown",
        payload
      );

      const markdown = response.data?.markdown ?? "";
      if (!markdown) {
        setError("The conversion returned an empty result.");
        setLoading(false);
        return;
      }

      onImported(markdown);
      reset();
      onClose();
    } catch (e: any) {
      trackSentryException(e);
      setError(
        e?.response?.data?.detail ??
          e?.message ??
          "Conversion failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }, [
    organisationId,
    tab,
    text,
    csvFile,
    imageFiles,
    onImported,
    onClose,
    reset,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="markdown-import-dialog"
    >
      <DialogTitle id="markdown-import-dialog">
        Import to markdown test
      </DialogTitle>
      <DialogContent dividers>
        <Tabs
          value={tab}
          onChange={(_, value: ImportTab) => {
            setTab(value);
            setError(null);
          }}
          className="mb-4"
        >
          <Tab value="text" label="Text" />
          <Tab value="csv" label="CSV" />
          <Tab value="image" label="Images" />
        </Tabs>

        {tab === "text" && (
          <TextField
            label="Paste test description"
            placeholder="Paste a test plan, ticket description, or step list…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            multiline
            minRows={8}
            fullWidth
          />
        )}

        {tab === "csv" && (
          <Box>
            <Button variant="outlined" component="label">
              {csvFile ? csvFile.name : "Choose CSV file"}
              <input
                type="file"
                accept=".csv,text/csv"
                hidden
                onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
              />
            </Button>
            <Box mt={1} sx={{ color: "text.secondary", fontSize: 13 }}>
              We&apos;ll send the CSV contents to the import agent which will
              translate the rows into markdown test steps.
            </Box>
          </Box>
        )}

        {tab === "image" && (
          <Box>
            <Button variant="outlined" component="label">
              {imageFiles.length > 0
                ? `${imageFiles.length} image(s) selected`
                : "Choose images"}
              <input
                type="file"
                accept="image/*"
                hidden
                multiple
                onChange={(e) =>
                  setImageFiles(Array.from(e.target.files ?? []))
                }
              />
            </Button>
            <Box mt={1} sx={{ color: "text.secondary", fontSize: 13 }}>
              Screenshots, mockups, or whiteboard photos. The agent will infer
              steps and generate a markdown test plan.
            </Box>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={submit}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {loading ? "Converting…" : "Convert to markdown"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
